import { Table } from 'antd';
import React, { useEffect, useState } from 'react';
import './Settings.scss';
import { ReactComponent as SearchIcon } from '@iba-team/portal-react-components/dist/svg/outline/search.svg';
import {
  Button,
  SearchInput,
  Select,
  Layout,
  Spinner,
  Alert,
  EmptyState,
  CustomPagination
} from '@iba-team/portal-react-components/dist';
import { Trans, useTranslation } from 'react-i18next';
import SettingsStore, { SettingEmployeeT } from '../../store/Settings';
import { useEffectOnce } from 'react-use';
import { CountryListStore } from '../../store/CountryList';
import { ColumnsType } from 'antd/es/table';
import ChoiseExp, {
  NO_SERVICE_LENGTH,
  SERVICE_LENGTH_GREATER_OR_EQUAL_N_YEARS,
  SERVICE_LENGTH_LESS_N_YEARS
} from './ChoiseExp';
import { observer } from 'mobx-react';
import { format } from 'date-fns';
import { useAnalyticsEventTracker } from '../../analytics/analytics';
import Sorter from '../../utils_components/Sorter';

type SelectType = {
  text: string,
  value: number
}

export const parseType  = (type: number) => {
  switch (type){
  case 8:
    return {
      type: SERVICE_LENGTH_GREATER_OR_EQUAL_N_YEARS,
      translation: 'workExperienceMore'
    };
  case 9:
    return {
      type: SERVICE_LENGTH_LESS_N_YEARS,
      translation: 'workExperienceLess',
    };
  case 10:
    return {
      type: NO_SERVICE_LENGTH,
      translation: 'noWorkingExp'
    };
  }
};

export const Settings = observer(() => {
  const {t} = useTranslation();
  const {
    getCurrentList, currentList, isFetched, updateCoefficients,
    submitList, fetchCoefficients, listCoefficients, updateConditionsForList,
    fetchSavedVacationSettings, isSavedStateFetched, savedState, updateSavedName, updateSavedOrder,
    updateSavedPage, updateSavedPaginateParams
  } = SettingsStore;
  const { getUserLocation } = CountryListStore;
  const {location} = CountryListStore;
  const [editable, setEditable] = useState(false);
  const [selectValue, setSelectValue] = useState<SelectType>();
  const gaEventTracker = useAnalyticsEventTracker('Vacation_options');

  useEffect(()=> {
    if (!isSavedStateFetched) {
      fetchSavedVacationSettings();
    }
  }, []);

  useEffectOnce(() => {
    Promise.all([getUserLocation(),fetchCoefficients()]);
  });

  useEffect(()=> {
    setEditable(currentList.hasHrChangedWorkExperience);
  }, [currentList.hasHrChangedWorkExperience]);
  const onSubmitList = ()=> {
    gaEventTracker('save_vacation_options');
    submitList({employeesCoefficientDtoList: currentList.list.map(item=> ({vacationSettingId: item.vacationSettingId, userId: item.userId})),
      order: savedState.employeesRequest.order !== null ? savedState.employeesRequest.order: 'ASC',
      page: savedState.employeesRequest.page,
      name: savedState.name,
      perPage: savedState.employeesRequest.perPage}).then(()=> setEditable(true));
  };

  useEffect(()=> {
    selectValue && updateConditionsForList(selectValue.value);
  },[selectValue]);

  useEffect(()=>{
    getCurrentList(savedState.employeesRequest.page,
      savedState.employeesRequest.perPage,
      savedState.name,
      savedState.employeesRequest.order !== null ? savedState.employeesRequest.order: 'ASC' );
  },[savedState.employeesRequest.page, savedState.employeesRequest.perPage, savedState.name, savedState.employeesRequest.order]);

  const selectArray: SelectType[] = [{
    text: t('workExperienceMore'),
    value: 8
  },
  {
    text: t('workExperienceLess'),
    value: 9
  },
  {
    text: t('noWorkingExp'),
    value: 10
  }
  ];

  const locale = {
    emptyText: (
      <EmptyState title={'No results found'}  description={' Please try searching with another name'}/>
    )
  };
  const columns: ColumnsType<SettingEmployeeT> = [
    {
      title: <div style={{width: '100px'}}>
        <Sorter text={t('employee')}
          sortOrder={savedState.employeesRequest.order === 'ASC' ? 1 : savedState.employeesRequest.order === 'DESC' ? -1 : 0 }
        />
      </div>,
      onHeaderCell: (_) => {
        const newOrder = savedState.employeesRequest.order === null ? 'ASC' : savedState.employeesRequest.order === 'ASC' ? 'DESC' : null;
        return {
          onClick: () => updateSavedOrder(newOrder)
        };

      },
      width: 640,
      render: ((_, item) => <div className={'employeeColumn'}>
        <span>{item.lastName}</span> <span>{item.firstName}</span>
      </div>)
    },
    {
      title: t('vacationOptions'),
      width: 820,
      render: ((_, elem) => {
        const type = parseType(elem.vacationSettingId);
        return editable ?
          <div>{type && t(type.translation)}</div>
          :
          <ChoiseExp
            coefList={listCoefficients}
            location={location}
            user={elem}
            onChangeRadio={updateCoefficients}
          />;
      })
    },
    {
      title: <span>{t('hireDate')}</span>,
      width: 160,
      dataIndex: 'hireDate',
      render: ((_, item) => <div>{format(new Date(item.hireDate), 'dd.MM.yyyy')}</div>)
    },
    {
      title: <span>{t('daysAvailable')}</span> ,
      dataIndex: 'availableDays',
      width: 200,
      render: ((availableDays) => <div className={'availableDaysColumn'}>
        <span>{availableDays}</span>
      </div>)
    },
  ];

  return (
    <Layout header={{layoutName: <div>
      {editable ? <div><h2>{t('workingExp')}</h2></div>
        :
        <div style={{display: 'flex'}}>
          <h2>Vacation Settings</h2> <h2 className="settings_location">{location }</h2>
        </div>}
    </div>}} className='settings'>
      <div className='settings_title'>
        {editable && currentList.lastUpdatedAt && <span className='settings_date'>{t('lastChanges', {date: format(new Date(currentList.lastUpdatedAt), 'dd.MM.yyyy HH:mm')})}</span>}
      </div>
      {!currentList.hasHrChangedWorkExperience && <Alert  type="warning" showIcon  >
        <Trans i18nKey={'warningSettings'} values={{location: location}}>
          <span className="vacationSettings"/>
        </Trans>
      </Alert>}
      <div className='settings_navigate'>
        <SearchInput value={savedState.name} className={'search_input'} allowClear
          placeholder={t('searchForEmployee')}
          prefix={<SearchIcon />}
          onChange={(event) => {
            updateSavedName(event);
          }} />
        <div className='settings_navigate_buttons'>
          {editable ?
            <Button theme={{variant: 'secondary', color:'blue'}} onClick={()=> setEditable(false)}>{t('settingsEditExp')}</Button>
            :
            <>
              <Select
                customDropdownWidth={'120px'}
                list={selectArray}
                uniqueElement={el => el.text === selectValue?.text}
                render={(item) => <div>{item.text}</div>}
                defaultValue={t('setAll')}
                value={selectValue?.text || ''}
                onChangeValue={(item) =>  setSelectValue(item)}
              />
              <Button theme={{variant: 'primary', color:'theme'}} onClick={()=> onSubmitList()}>{t('saveAndApply')}</Button>
            </>
          }
        </div>
      </div>
      <Table
        locale={locale}
        dataSource={currentList.list}
        loading={{
          spinning: !isFetched,
          indicator: <Spinner/>,
        }}
        columns={columns}
        bordered
        pagination={false}
      />
      <CustomPagination {...{
        total: currentList?.total,
        current: savedState.employeesRequest.page,
        defaultCurrent: savedState.employeesRequest.page,
        position: ['bottomLeft'],
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100'],
        onShowSizeChange: (page, size) => {
          updateSavedPaginateParams(size, page);
        },
        pageSize: savedState.employeesRequest.perPage,
        onChange: (page) => updateSavedPage(page),
        hideOnSinglePage: currentList?.total <= 10,
        showTotal: undefined
      }}/>
    </Layout>
  );
});


